import { useState } from "react";
import { IMG_URl } from "../../../common/img_url";
import { Trash } from "lucide-react";
import { indexStore } from "../../../store/indexStore";
import Visibility from "../../utils/Visibility";
import { useNavigate, useParams } from "react-router-dom";
import UAEDirhamIcon from "../../utils/UAEDirhamIcon";

const CartItem = ({
  element,
  handleRemoveItem,
  handleUpdateQuantity,
}: {
  element: any;
  handleRemoveItem: (item: any) => void;
  handleUpdateQuantity: (item: any, quantity: number) => void;
}) => {
  const [quantity, setQuantity] = useState(element.amount || 1);
  const { name } = indexStore();
  const calculateTotalPrice = () => {
    let totalPrice = element.price;
    if (element.option) {
      element.option.forEach((opt: any) => {
        if (opt.subOption) {
          totalPrice += opt.subOption.price;
        }
      });
    }
    return totalPrice * quantity;
  };

  // دوال لتحديث الكمية
  const handleIncrement = () => {
    const newQuantity = quantity + 1;
    setQuantity(newQuantity);
    handleUpdateQuantity(element, newQuantity);
  };

  const handleDecrement = () => {
    if (quantity > 1) {
      const newQuantity = quantity - 1;
      setQuantity(newQuantity);
      handleUpdateQuantity(element, newQuantity);
    }
  };
  // تجميع الخيارات حسب الـ option
  const groupedOptions = () => {
    if (!element.option || element.option.length === 0) return [];
    
    const grouped: { [key: number]: { optionName: string; subOptions: any[] } } = {};
    
    element.option.forEach((opt: any) => {
      const optionId = opt.option?.id;
      if (!optionId) return;
      
      if (!grouped[optionId]) {
        grouped[optionId] = {
          optionName: opt.option[name] || "",
          subOptions: [],
        };
      }
      
      // TypeScript type guard - نضمن أن grouped[optionId] موجود
      const currentGroup = grouped[optionId];
      if (currentGroup && opt.subOption) {
        currentGroup.subOptions.push(opt.subOption);
      }
    });
    
    return Object.values(grouped);
  };

  const renderOptions = () => {
    const grouped = groupedOptions();
    
    return grouped.map((group, groupIndex) => (
      <div key={groupIndex} className="flex flex-col gap-2">
        <span className="text-xs md:text-sm font-semibold text-foreground">
          {group.optionName}:
        </span>
        <div className="flex flex-wrap gap-1.5">
          {group.subOptions.map((subOpt: any, subIndex: number) => (
            <div
              key={subIndex}
              className="inline-flex items-center gap-1.5 px-2.5 py-1.5 bg-primary/10 text-primary border border-primary/30 rounded-lg text-xs md:text-sm font-medium"
            >
              <span>{subOpt[name]}</span>
              <Visibility visible={subOpt.price > 0}>
                <span dir="ltr" className="flex items-center gap-0.5 text-primary font-semibold">
                  (+
                  <UAEDirhamIcon className="w-3 h-3" />
                  {subOpt.price})
                </span>
              </Visibility>
            </div>
          ))}
        </div>
      </div>
    ));
  };
  const navigate = useNavigate();
  const params = useParams();

  const navegateToProduct = (id: number) => {
    navigate(
      `/branches/${params.branch_id}/${element.category?.section_id}/${id}`
    );
  };

  return (
    <div className="flex flex-col sm:flex-row gap-4 md:gap-5 p-4 md:p-5 bg-gradient-to-br from-card to-background rounded-2xl shadow-md hover:shadow-xl border-2 border-border/50 transition-all duration-300">
      <div className="flex items-start gap-3 md:gap-4 flex-grow min-w-0">
        <img
          src={`${IMG_URl}${element.image}`}
          alt="img"
          className="object-cover rounded-xl w-20 h-20 md:w-24 md:h-24 flex-shrink-0 cursor-pointer hover:scale-105 transition-transform"
          onClick={() => navegateToProduct(element.id)}
        />

        <div className="flex flex-col gap-2.5 flex-1 min-w-0">
          <h3
            className="text-base md:text-lg font-bold text-foreground cursor-pointer hover:text-primary transition-colors line-clamp-2"
            onClick={() => navegateToProduct(element.id)}
          >
            {element[name]}
          </h3>
          
          <Visibility visible={element.option && element.option.length > 0}>
            <div className="flex flex-col gap-2.5">
              {renderOptions()}
            </div>
          </Visibility>

          <div className="flex items-center text-foreground gap-2 text-sm md:text-base font-semibold bg-muted/20 rounded-lg px-3 py-1.5 w-fit">
            <span className="text-primary font-bold">{quantity}</span>
            <span className="text-muted-foreground">×</span>
            <span className="text-foreground">{(calculateTotalPrice() / quantity).toFixed(2)}</span>
            <span className="text-muted-foreground">=</span>
            <span className="flex items-center gap-1 text-primary font-bold">
              <UAEDirhamIcon className="w-4 h-4 md:w-5 md:h-5" />
              {calculateTotalPrice().toFixed(2)}
            </span>
          </div>
          
          <Visibility visible={!!element.note}>
            <div className="flex items-start gap-2 p-2 bg-primary/5 rounded-lg border border-primary/10">
              <span className="text-xs font-semibold text-primary">ملاحظة:</span>
              <p className="text-xs md:text-sm text-foreground/80 flex-1">
                {element.note}
              </p>
            </div>
          </Visibility>
        </div>
      </div>

      <div className="flex items-center gap-3 sm:gap-4 justify-between sm:justify-end">
        <div className="flex items-center gap-2 md:gap-3 bg-muted/30 rounded-xl p-1">
          <button
            onClick={handleDecrement}
            disabled={quantity <= 1}
            className="bg-primary text-onPrimary px-3 py-1.5 md:px-4 md:py-2 rounded-lg text-lg font-bold hover:bg-primary/90 active:scale-95 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200 shadow-sm"
          >
            -
          </button>
          <span className="text-base md:text-lg font-bold text-foreground min-w-[2.5rem] text-center">
            {quantity}
          </span>
          <button
            onClick={handleIncrement}
            className="bg-primary text-onPrimary px-3 py-1.5 md:px-4 md:py-2 rounded-lg text-lg font-bold hover:bg-primary/90 active:scale-95 transition-all duration-200 shadow-sm"
          >
            +
          </button>
        </div>
        <button
          className="flex items-center justify-center p-2.5 md:p-3 bg-destructive/10 hover:bg-destructive/20 active:scale-95 text-primary rounded-xl cursor-pointer transition-all duration-200 border border-destructive/20"
          onClick={() => handleRemoveItem(element)}
        >
          <Trash className="w-5 h-5 md:w-6 md:h-6" />
        </button>
      </div>
    </div>
  );
};

export default CartItem;
